<template>
  <header class="topbar">
    <!-- 全局搜索 -->
    <div ref="searchRef" class="topbar__search" :class="{ 'is-open': searchOpen }">
      <el-icon class="topbar__search-icon"><Search /></el-icon>
      <input
        ref="searchInput"
        v-model="keyword"
        class="topbar__search-input"
        placeholder="搜索门店、品牌"
        @focus="onSearchFocus"
        @input="searchOpen = true"
      />
      <kbd class="topbar__kbd">⌘K</kbd>

      <div v-if="searchOpen" class="search-panel">
        <template v-if="keyword.trim()">
          <template v-if="hits.stores.length">
            <p class="search-group">门店</p>
            <button
              v-for="s in hits.stores"
              :key="s.storeEntCode"
              class="search-item"
              @click="goStore(s)"
            >
              <el-icon><Shop /></el-icon>
              <span class="search-item__main">{{ s.storeEntName || '未命名门店' }}</span>
              <span class="search-item__code num">{{ s.storeEntCode }}</span>
            </button>
          </template>

          <template v-if="hits.brands.length">
            <p class="search-group">品牌</p>
            <button
              v-for="b in hits.brands"
              :key="b.brandCode"
              class="search-item"
              @click="goBrand(b)"
            >
              <el-icon><Shop /></el-icon>
              <span class="search-item__main">{{ b.brandName || '未命名品牌' }}</span>
              <span class="search-item__code num">{{ b.brandCode }}</span>
            </button>
          </template>

          <p v-if="!hits.stores.length && !hits.brands.length" class="search-empty">
            未找到匹配结果
          </p>
        </template>

        <p v-else class="search-tip">输入关键字开始搜索</p>
      </div>
    </div>

    <div class="topbar__right">
      <!-- 待办通知 -->
      <el-dropdown trigger="click" placement="bottom-end" @visible-change="loadTodos">
        <button class="icon-btn">
          <el-icon><Bell /></el-icon>
          <span v-if="todoCount > 0" class="icon-btn__dot"></span>
        </button>
        <template #dropdown>
          <el-dropdown-menu>
            <li class="dd-title">待办通知</li>
            <el-dropdown-item v-for="t in todos" :key="t.id" @click="goTodo">
              <span class="dd-text">{{ todoTitle(t) }}</span>
              <span class="dd-extra">{{ todoDue(t) }}</span>
            </el-dropdown-item>
            <li v-if="!todos.length" class="dd-empty">暂无待办</li>
          </el-dropdown-menu>
        </template>
      </el-dropdown>

      <!-- 用户 -->
      <el-dropdown trigger="click" placement="bottom-end">
        <div class="user-btn">
          <span class="user-avatar">{{ avatarText }}</span>
          <span class="user-name">{{ displayName }}</span>
          <el-icon class="user-arrow"><ArrowDown /></el-icon>
        </div>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item @click="showPasswordDialog">
              <el-icon><EditPen /></el-icon><span>修改密码</span>
            </el-dropdown-item>
            <el-dropdown-item divided @click="handleLogout">
              <el-icon><SwitchButton /></el-icon><span>退出登录</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <!-- 修改密码 -->
    <el-dialog v-model="passwordDialogVisible" title="修改密码" width="400px" destroy-on-close>
      <el-form
        ref="passwordFormRef"
        :model="passwordForm"
        :rules="passwordRules"
        label-position="top"
      >
        <el-form-item label="旧密码" prop="oldPassword">
          <el-input v-model="passwordForm.oldPassword" type="password" show-password placeholder="请输入旧密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" show-password placeholder="请输入新密码" />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" show-password placeholder="请确认新密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleUpdatePassword">保存</el-button>
      </template>
    </el-dialog>
  </header>
</template>

<script>
import { defineComponent, ref, computed, reactive, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Search, Bell, ArrowDown, EditPen, SwitchButton, Shop } from '@element-plus/icons-vue'
import assetService from '../../services/assetService'
import postInvestService from '../../services/postInvestService'
import userService from '../../services/userService'
import authService from '@/services/authService'
import { setUserInfo } from '@/utils/auth'
import { useStore } from 'vuex'

// 搜索数据只加载一次，之后前端过滤
let cachedStores = null
let cachedBrands = null

const pickList = (res) => {
  const data = res?.data
  if (Array.isArray(data)) return data
  return data?.storeList || data?.brandList || data?.list || []
}

export default defineComponent({
  name: 'LayoutHeader',
  components: { Search, Bell, ArrowDown, EditPen, SwitchButton, Shop },
  setup() {
    const router = useRouter()
    const store = useStore()

    const searchRef = ref(null)
    const searchInput = ref(null)
    const searchOpen = ref(false)
    const keyword = ref('')
    const stores = ref([])
    const brands = ref([])

    const todos = ref([])
    const todoCount = ref(0)

    const userInfo = ref(store.state.userInfo)
    const passwordDialogVisible = ref(false)
    const passwordFormRef = ref(null)
    const passwordForm = reactive({ oldPassword: '', newPassword: '', confirmPassword: '' })

    const passwordRules = {
      oldPassword: [{ required: true, message: '请输入旧密码', trigger: 'blur' }],
      newPassword: [
        { required: true, message: '请输入新密码', trigger: 'blur' },
        { min: 6, message: '新密码长度不能少于6位', trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, message: '请确认新密码', trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (value !== passwordForm.newPassword) callback(new Error('两次输入的密码不一致'))
            else callback()
          },
          trigger: 'blur'
        }
      ]
    }

    const displayName = computed(() => userInfo.value?.realName || userInfo.value?.username || '未登录')
    const avatarText = computed(() => (displayName.value || 'U').trim().charAt(0).toUpperCase())

    const hits = computed(() => {
      const k = keyword.value.trim().toLowerCase()
      if (!k) return { stores: [], brands: [] }
      return {
        stores: stores.value
          .filter(s => (s.storeEntName || '').toLowerCase().includes(k) || (s.storeEntCode || '').toLowerCase().includes(k))
          .slice(0, 6),
        brands: brands.value
          .filter(b => (b.brandName || '').toLowerCase().includes(k) || (b.brandCode || '').toLowerCase().includes(k))
          .slice(0, 4)
      }
    })

    const loadSearchData = async () => {
      if (cachedStores && cachedBrands) {
        stores.value = cachedStores
        brands.value = cachedBrands
        return
      }
      try {
        const [sRes, bRes] = await Promise.all([
          assetService.getStoreList({ pageNum: 1, pageSize: 300 }),
          assetService.getBrandList({ pageNum: 1, pageSize: 300 })
        ])
        cachedStores = pickList(sRes)
        cachedBrands = pickList(bRes)
        stores.value = cachedStores
        brands.value = cachedBrands
      } catch {
        // 搜索数据加载失败不阻断使用，静默降级
      }
    }

    const onSearchFocus = async () => {
      searchOpen.value = true
      await loadSearchData()
    }

    const goStore = (s) => {
      searchOpen.value = false
      keyword.value = ''
      router.push({ name: 'storeDetail', params: { storeEntCode: s.storeEntCode } })
    }

    const goBrand = (b) => {
      searchOpen.value = false
      keyword.value = ''
      router.push({ name: 'brandDetail', params: { brandCode: b.brandCode } })
    }

    const goTodo = () => router.push('/afterInvest/todo')

    const todoTitle = (t) => t.title || t.todoTitle || t.content || '未命名待办'
    const todoDue = (t) => t.dueDate || t.deadline || ''

    const loadTodos = async (visible) => {
      if (!visible) return
      try {
        const res = await postInvestService.getTodoList({ tenantId: store.state.tenantId })
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.list || [])
        todos.value = list.slice(0, 6)
        todoCount.value = list.length
      } catch {
        todos.value = []
        todoCount.value = 0
      }
    }

    const handleLogout = () => {
      store.dispatch('logout')
      router.push('/login')
      ElMessage.success('退出登录成功')
    }

    const showPasswordDialog = () => {
      passwordDialogVisible.value = true
      passwordForm.oldPassword = ''
      passwordForm.newPassword = ''
      passwordForm.confirmPassword = ''
      nextTick(() => passwordFormRef.value?.resetFields())
    }

    const handleUpdatePassword = async () => {
      if (!passwordFormRef.value) return
      try {
        await passwordFormRef.value.validate()
        const result = await userService.updatePassword({
          userId: userInfo.value?.id,
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.newPassword
        })
        if (result.code === 200) {
          ElMessage.success('密码修改成功')
          passwordDialogVisible.value = false
        } else {
          ElMessage.error(result.msg || '密码修改失败')
        }
      } catch (error) {
        if (error?.message) ElMessage.error('修改密码失败，请重试')
      }
    }

    const fetchUserInfo = async () => {
      try {
        const info = await authService.getUserInfo()
        store.commit('SET_USER_INFO', info)
        userInfo.value = info
        setUserInfo(info)
      } catch (error) {
        console.error('获取用户信息失败', error)
      }
    }

    // ⌘K / Ctrl+K 聚焦搜索
    const onKeydown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        searchInput.value?.focus()
      }
      if (e.key === 'Escape') searchOpen.value = false
    }

    // 点击外部关闭搜索面板
    const onDocClick = (e) => {
      if (searchRef.value && !searchRef.value.contains(e.target)) searchOpen.value = false
    }

    onMounted(() => {
      if (!userInfo.value) fetchUserInfo()
      loadTodos(true)
      document.addEventListener('keydown', onKeydown)
      document.addEventListener('click', onDocClick)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', onKeydown)
      document.removeEventListener('click', onDocClick)
    })

    return {
      searchRef, searchInput, searchOpen, keyword, hits, onSearchFocus, goStore, goBrand,
      todos, todoCount, loadTodos, goTodo, todoTitle, todoDue,
      displayName, avatarText,
      passwordDialogVisible, passwordFormRef, passwordForm, passwordRules,
      showPasswordDialog, handleUpdatePassword, handleLogout
    }
  }
})
</script>

<style scoped>
.topbar {
  height: 56px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
}

/* ---------- 搜索 ---------- */
.topbar__search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 260px;
  padding: 6px 10px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-page);
  transition: border-color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease);
}

.topbar__search.is-open,
.topbar__search:focus-within {
  border-color: var(--brand-500);
  background: var(--bg-card);
}

.topbar__search-icon {
  color: var(--text-tertiary);
  font-size: 14px;
  flex: 0 0 14px;
}

.topbar__search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 12px;
  color: var(--text-primary);
}

.topbar__search-input::placeholder {
  color: var(--text-disabled);
}

.topbar__kbd {
  font-size: 10px;
  color: var(--text-disabled);
  border: 1px solid var(--border-strong);
  border-radius: 3px;
  padding: 0 4px;
  font-family: inherit;
}

.search-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  min-width: 320px;
  max-height: 380px;
  overflow-y: auto;
  padding: 6px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-lg);
  z-index: 2000;
}

.search-group {
  font-size: 11px;
  color: var(--text-tertiary);
  padding: 8px 10px 4px;
  margin: 0;
}

.search-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: none;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text-primary);
  font-size: 13px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.search-item:hover {
  background: var(--bg-page);
}

.search-item__main {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-item__code {
  font-size: 11px;
  color: var(--text-tertiary);
}

.search-tip,
.search-empty {
  padding: 18px 12px;
  margin: 0;
  text-align: center;
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ---------- 右侧 ---------- */
.topbar__right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
}

.icon-btn {
  position: relative;
  display: flex;
  align-items: center;
  padding: 6px;
  border: none;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text-secondary);
  font-size: 17px;
  cursor: pointer;
}

.icon-btn:hover {
  background: var(--bg-page);
  color: var(--text-primary);
}

.icon-btn__dot {
  position: absolute;
  top: 4px;
  right: 5px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--danger-fill);
}

.user-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: var(--radius-control);
  cursor: pointer;
  outline: none;
}

.user-btn:hover {
  background: var(--bg-page);
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--brand-100);
  color: var(--brand-700);
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-name {
  font-size: 13px;
  color: var(--text-primary);
}

.user-arrow {
  font-size: 12px;
  color: var(--text-tertiary);
}

/* 下拉菜单 */
:deep(.el-dropdown-menu__item) {
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dd-title {
  font-size: 11px;
  color: var(--text-tertiary);
  padding: 6px 14px;
  list-style: none;
}

.dd-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dd-extra {
  margin-left: 16px;
  font-size: 11px;
  color: var(--text-tertiary);
}

.dd-empty {
  padding: 16px 14px;
  font-size: 12px;
  color: var(--text-tertiary);
  text-align: center;
  list-style: none;
}
</style>
